import type { RequestPayload } from './types';

export type HttpMethod = 'GET' | 'POST' | 'DELETE';

export type Route = { path: string; httpMethod: HttpMethod; operation?: string };
export type RouteFor = Route | ((payload: RequestPayload) => Route);

// resource: the collection the default methods (get, query, create, update, delete, bulk) work on.
// routes: every other method, by name. Every path placeholder names the exact top-level payload field.
export type NamespaceRoutes = { resource?: string; routes?: Record<string, RouteFor> };

export const get = (path: string) => ({ path, httpMethod: 'GET' as const });
export const post = (path: string) => ({ path, httpMethod: 'POST' as const });
export const del = (path: string) => ({ path, httpMethod: 'DELETE' as const });

// For a call the service has no route for yet: it fails when it is made, saying which call it is.
export const unserved = (call: string) => () => {
  throw new Error(`The service has no route for ${call} yet`);
};

const fieldsOf = (payload: RequestPayload): Record<string, unknown> =>
  !Array.isArray(payload) && payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};

const routeValue = (value: unknown, name: string, path: string) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
  throw new Error(`Invalid route parameter ${name} for ${path}`);
};

const expand = (path: string, payload: RequestPayload) =>
  path.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = fieldsOf(payload)[name];
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing route parameter ${name} for ${path}`);
    }
    return encodeURIComponent(routeValue(value, name, path));
  });

const defaultRoute = (resource: string, method: string, id: string | undefined) => {
  if (method === 'create') return post(`/${resource}`);
  if (method === 'update' && id) return post(`/${resource}/${id}`);
  if (method === 'delete' && id) return del(`/${resource}/${id}`);
  if (method === 'get' && id) return get(`/${resource}/${id}`);
  if (method === 'query') return get(`/${resource}`);
  if (method === 'bulk') return post(`/${resource}/bulk`);
  return undefined;
};

export const resolveRoute = (namespace: NamespaceRoutes | undefined, method: string, payload: RequestPayload) => {
  const declared = namespace?.routes?.[method];
  if (declared) {
    const route = typeof declared === 'function' ? declared(payload) : declared;
    return { ...route, operation: route.operation || `${route.httpMethod} ${route.path}`, path: expand(route.path, payload) };
  }
  const { uuid } = fieldsOf(payload);
  if (!namespace?.resource) return undefined;
  const route = defaultRoute(namespace.resource, method, uuid === undefined ? undefined : routeValue(uuid, 'uuid', `/${namespace.resource}/{uuid}`));
  if (!route) return undefined;
  const template = method === 'create' || method === 'query' ? `/${namespace.resource}`
    : method === 'bulk' ? `/${namespace.resource}/bulk`
    : `/${namespace.resource}/{uuid}`;
  return { ...route, operation: `${route.httpMethod} ${template}` };
};
